/* =============================================================
   Psychological Safety — pure scoring + narrative engine.

   No React, no DOM, no Supabase. Easy to unit test. Same logic
   will run when we precompute scores at save time (server-side
   later if needed). See research-design-memo.md §5–§7 for design.
   ============================================================ */

import {
  BANDS,
  COMPOSITE_BODIES,
  COMPOSITE_HEADLINES,
  ITEMS_PER_STAGE,
  ITEM_THEMES,
  STAGES,
  STAGE_NARRATIVES,
  THEMES,
  TOTAL_ITEMS,
  type Band,
  type Stage,
  type StageId,
  type Theme,
} from '../data/psychSafety';

export type Responses = Record<string, number>;

/** Look up the band a stage average falls into. */
export function bandFor(avg: number): Band {
  return BANDS.find((b) => avg >= b.min && avg < b.max) ?? BANDS[BANDS.length - 1];
}

export interface ZoneCounts {
  red: number;
  neutral: number;
  blue: number;
  total: number;
  redPct: number;
  neutralPct: number;
  bluePct: number;
}

/** Bucket scores into red (1-2), neutral (3), blue (4-5). */
export function zoneCounts(scores: number[]): ZoneCounts {
  let red = 0,
    neutral = 0,
    blue = 0;
  for (const s of scores) {
    if (s <= 2) red++;
    else if (s === 3) neutral++;
    else blue++;
  }
  const total = scores.length;
  return {
    red,
    neutral,
    blue,
    total,
    redPct: total ? (red / total) * 100 : 0,
    neutralPct: total ? (neutral / total) * 100 : 0,
    bluePct: total ? (blue / total) * 100 : 0,
  };
}

/** Average a stage's answered items, treating unanswered as missing. */
export function stageAverage(responses: Responses, stage: Stage): number {
  const scores = stage.items
    .map((_, i) => responses[`${stage.id}:${i}`])
    .filter((v): v is number => typeof v === 'number' && v > 0);
  if (!scores.length) return 0;
  return scores.reduce((a, b) => a + b, 0) / scores.length;
}

/** Mean of stage means. */
export function overallAverage(responses: Responses): number {
  const stageAvgs = STAGES.map((s) => stageAverage(responses, s));
  return stageAvgs.reduce((a, b) => a + b, 0) / stageAvgs.length;
}

/** Map of "<stageId>: avg" for storage / fast access. */
export function allStageAverages(responses: Responses): Record<StageId, number> {
  const out = {} as Record<StageId, number>;
  for (const s of STAGES) out[s.id] = stageAverage(responses, s);
  return out;
}

/** Sum zone counts across every answered item in the whole assessment. */
export function overallZoneCounts(responses: Responses): ZoneCounts {
  const all = STAGES.flatMap((s) =>
    s.items.map((_, i) => responses[`${s.id}:${i}`]),
  ).filter((v): v is number => typeof v === 'number' && v > 0);
  return zoneCounts(all);
}

/** True when every item in every stage has a numeric answer. */
export function isComplete(responses: Responses): boolean {
  if (Object.keys(responses).length < TOTAL_ITEMS) return false;
  for (const s of STAGES) {
    for (let i = 0; i < ITEMS_PER_STAGE[s.id]; i++) {
      const v = responses[`${s.id}:${i}`];
      if (typeof v !== 'number' || v <= 0) return false;
    }
  }
  return true;
}

export function answeredCount(responses: Responses): number {
  let n = 0;
  for (const v of Object.values(responses)) {
    if (typeof v === 'number' && v > 0) n++;
  }
  return n;
}

// ============================================================
// Per-stage detail (used by the narrative)
// ============================================================
export interface ItemRow {
  idx: number;
  text: string;
  score: number;
}

export interface PerStage {
  stageId: StageId;
  name: string;
  subtitle: string;
  accent: string;
  avg: number;
  band: Band;
  zones: ZoneCounts;
  narrative: string;
  /** Up to 3 lowest items where score ≤ 3 — the growth opportunities. */
  lowest: ItemRow[];
  /** Up to 2 highest items where score ≥ 4 — "already strong". */
  highest: ItemRow[];
}

function perStageDetail(responses: Responses, stage: Stage): PerStage {
  const scores = stage.items.map((_, i) => responses[`${stage.id}:${i}`] ?? 0);
  const valid = scores.filter((s) => s > 0);
  const avg = valid.length ? valid.reduce((a, b) => a + b, 0) / valid.length : 0;
  const band = bandFor(avg);
  const zones = zoneCounts(valid);

  const indexed: ItemRow[] = scores.map((v, i) => ({
    idx: i,
    text: stage.items[i],
    score: v,
  }));
  const lowest = [...indexed]
    .filter((r) => r.score > 0)
    .sort((a, b) => a.score - b.score)
    .slice(0, 3)
    .filter((r) => r.score <= 3);
  const highest = [...indexed]
    .filter((r) => r.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 2)
    .filter((r) => r.score >= 4);

  return {
    stageId: stage.id,
    name: stage.name,
    subtitle: stage.subtitle,
    accent: stage.accent,
    avg,
    band,
    zones,
    narrative: STAGE_NARRATIVES[stage.id][band.key],
    lowest,
    highest,
  };
}

// ============================================================
// Themes
// ============================================================
export interface ThemeScore extends Theme {
  avg: number;
  band: Band;
  /** Number of items tagged with this theme that were answered. */
  count: number;
}

function themeScores(responses: Responses): ThemeScore[] {
  return THEMES.map((t) => {
    const scores: number[] = [];
    for (const [key, themes] of Object.entries(ITEM_THEMES)) {
      if (!themes.includes(t.id)) continue;
      const v = responses[key];
      if (typeof v === 'number' && v > 0) scores.push(v);
    }
    const avg = scores.length ? scores.reduce((a, b) => a + b, 0) / scores.length : 0;
    return { ...t, avg, band: bandFor(avg), count: scores.length };
  });
}

// ============================================================
// Flags
// ============================================================
export type FlagSeverity = 'high' | 'medium';
export type FlagType = 'foundation' | 'progression';

export interface Flag {
  type: FlagType;
  severity: FlagSeverity;
  title: string;
  message: string;
}

function computeFlags(perStage: PerStage[]): Flag[] {
  const flags: Flag[] = [];

  // Foundation alert: Inclusion in any "Red" zone.
  const inclusion = perStage[0];
  if (inclusion.band.zone.toLowerCase().includes('red')) {
    flags.push({
      type: 'foundation',
      severity: 'high',
      title: 'Foundation alert',
      message:
        "Inclusion safety scores below the neutral zone. Clark's framework is built on the idea that everything above inclusion sits on it — until belonging is solid, the safety to learn, contribute, and challenge is structurally unstable, even when those stages score higher.",
    });
  }

  // Progression note: worst case of upper-stage outscoring lower-stage by > 0.5.
  let worstInversion: { upper: PerStage; lower: PerStage; delta: number } | null = null;
  for (let i = 1; i < perStage.length; i++) {
    for (let j = 0; j < i; j++) {
      const delta = perStage[i].avg - perStage[j].avg;
      if (delta > 0.5 && (!worstInversion || delta > worstInversion.delta)) {
        worstInversion = { upper: perStage[i], lower: perStage[j], delta };
      }
    }
  }
  if (worstInversion) {
    flags.push({
      type: 'progression',
      severity: 'medium',
      title: `Progression note — ${worstInversion.upper.name} outranks ${worstInversion.lower.name}`,
      message:
        `You score noticeably higher on ${worstInversion.upper.name} (${worstInversion.upper.avg.toFixed(2)}) than on ${worstInversion.lower.name} (${worstInversion.lower.avg.toFixed(2)}). ` +
        "In Clark's framework the stages are successive — each rests on the ones beneath it. A higher score on the upper stage usually means you're scoring yourself on intent rather than on the foundation those behaviours actually need.",
    });
  }

  return flags;
}

// ============================================================
// Top focus
// ============================================================
export interface TopFocus {
  stage: PerStage;
  itemIdx: number;
  itemText: string;
  itemScore: number;
  action: string;
}

function computeTopFocus(perStage: PerStage[]): TopFocus | null {
  // Default: lowest-scoring stage. But if inclusion is in red, start there.
  let focus = perStage[0];
  for (const ss of perStage) {
    if (ss.avg < focus.avg) focus = ss;
    if (ss.band.zone.toLowerCase().includes('red')) {
      focus = ss;
      break;
    }
  }
  const item = focus.lowest[0];
  if (!item) return null;
  return {
    stage: focus,
    itemIdx: item.idx,
    itemText: item.text,
    itemScore: item.score,
    action: item.text.charAt(0).toUpperCase() + item.text.slice(1),
  };
}

// ============================================================
// Narrative — the everything function
// ============================================================
export interface Narrative {
  overall: {
    score: number;
    band: Band;
    headline: string;
    body: string;
    zones: ZoneCounts;
  };
  perStage: PerStage[];
  flags: Flag[];
  themes: ThemeScore[];
  weakestTheme: ThemeScore | null;
  strongestTheme: ThemeScore | null;
  themeSpread: number;
  topFocus: TopFocus | null;
}

export function generateNarrative(responses: Responses): Narrative {
  const perStage = STAGES.map((s) => perStageDetail(responses, s));

  const overall = perStage.reduce((s, ss) => s + ss.avg, 0) / perStage.length;
  const overallBand = bandFor(overall);
  const overall_zones = overallZoneCounts(responses);

  const themes = themeScores(responses);
  const sortedThemes = [...themes].filter((t) => t.count > 0).sort((a, b) => a.avg - b.avg);
  const weakestTheme = sortedThemes[0] ?? null;
  const strongestTheme = sortedThemes[sortedThemes.length - 1] ?? null;
  const themeSpread =
    strongestTheme && weakestTheme ? strongestTheme.avg - weakestTheme.avg : 0;

  // Headline override: if the lowest and highest stages are >1.0 apart,
  // call out the gap rather than just labeling the composite band.
  let headline = COMPOSITE_HEADLINES[overallBand.key];
  const sortedByAvg = [...perStage].sort((a, b) => a.avg - b.avg);
  const lowestStage = sortedByAvg[0];
  const highestStage = sortedByAvg[sortedByAvg.length - 1];
  if (highestStage.avg - lowestStage.avg > 1.0) {
    headline = `Strong in ${highestStage.name.replace(' Safety', '')}, with a notable gap in ${lowestStage.name.replace(
      ' Safety',
      '',
    )}.`;
  }

  return {
    overall: {
      score: overall,
      band: overallBand,
      headline,
      body: COMPOSITE_BODIES[overallBand.key],
      zones: overall_zones,
    },
    perStage,
    flags: computeFlags(perStage),
    themes,
    weakestTheme,
    strongestTheme,
    themeSpread,
    topFocus: computeTopFocus(perStage),
  };
}
